import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createCohort,
  createCohortEditStep,
  createCohortNextStep,
  createCohortRunJob,
} from './fetchers';
import { CohortStepData, CohortStepType } from 'src/types/cohorts';
import { CreateCodesetInput } from 'src/components/dataset/create-cohort/steps/codes-selection-components/code-sets.dto';
import { createCodeset, updateCodeset } from 'src/api/codesets';
import { UpdateCodesetDto } from 'src/components/dataset/create-cohort/steps/codes-selection-components/code-sets.dto';

export const useCreateCodesetMutation = () => {
  return useMutation({
    mutationFn: (input: CreateCodesetInput) => createCodeset(input),
  });
};

export const useUpdateCodesetMutation = () => {
  return useMutation({
    mutationFn: ({
      organizationId,
      datasourceId,
      id,
      updateDto,
    }: {
      organizationId: string;
      datasourceId: string;
      id: string;
      updateDto: UpdateCodesetDto;
    }) => updateCodeset(organizationId, datasourceId, id, updateDto),
  });
};

export const useCreateCohortMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dataSourceId, query }: { dataSourceId: string; query: string }) =>
      createCohort({ dataSourceId, query }),
    onSuccess: () => {
      // Invalidate cohort queries (invalidateQueries automatically refetches active queries)
      queryClient.invalidateQueries({ queryKey: ['data-source-cohorts'] });
    },
  });
};

export const useCreateCohortNextStepMutation = () => {
  return useMutation({
    mutationFn: ({
      dataSourceId,
      cohortId,
      currentStep,
      steps,
      organizationId,
    }: {
      dataSourceId: string;
      cohortId: string;
      currentStep: CohortStepType;
      steps: Record<CohortStepType, CohortStepData>;
      organizationId: string | null;
    }) => createCohortNextStep({ dataSourceId, cohortId, currentStep, steps, organizationId }),
  });
};

export const useCreateCohortEditStepMutation = () => {
  return useMutation({
    mutationFn: ({
      dataSourceId,
      cohortId,
      step,
      editedContent,
    }: {
      dataSourceId: string;
      cohortId: string;
      step: CohortStepType;
      editedContent: string;
    }) => createCohortEditStep({ dataSourceId, cohortId, step, editedContent }),
  });
};

export const useCreateCohortRunJobMutation = () => {
  return useMutation({
    mutationFn: ({
      dataSourceId,
      cohortId,
      steps,
    }: {
      dataSourceId: string;
      cohortId: string;
      steps: Record<CohortStepType, CohortStepData>;
    }) => createCohortRunJob({ dataSourceId, cohortId, steps }),
  });
};
